import { useState, useEffect, ChangeEvent, DragEvent, SyntheticEvent, useCallback, ReactNode } from 'react';
import { Grid } from '@mui/material';
import { PostArtifact } from '@models/note';
import { getPostArtifact, isDataTransfer } from '@utils/fileUtils';
import { IncludeDocuments } from '@components/dialog/IncludeDocuments';
import { UploadFilesView } from '@components/dialog/UploadFilesView';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useParams } from 'react-router-dom';
import { DocumentSearchResponse } from '@lio/api-types';

import { Dialog } from './Dialog';
import { MAX_FILES_AMOUNT } from '@components/drawerItem/DrawerItem.constants';

interface AddFilesDialogProps {
  open: boolean;
  onDone: (args: { uploadedFiles: PostArtifact[]; includedDocuments: DocumentSearchResponse['items'] }) => void;
  draggedFiles?: File[];
  resetDraggedFiles?: () => void;
  uploadedFiles?: PostArtifact[];
  handleCloseDialog: (e?: SyntheticEvent) => void;
  maxFiles?: number;
  hideIncludedDocumentsSection?: boolean;
  /** Optional content rendered below the upload area, e.g. an additional opt-in checkbox. */
  additionalContent?: ReactNode;
}

export const AddFilesDialog = ({
  open,
  handleCloseDialog,
  onDone,
  draggedFiles = [],
  resetDraggedFiles,
  uploadedFiles = [],
  maxFiles = MAX_FILES_AMOUNT,
  hideIncludedDocumentsSection = true,
  additionalContent,
}: AddFilesDialogProps) => {
  const { quoteId } = useParams();
  const [, userAuth] = useStore((state) => [state.currentUser, state.userAuth], shallow);
  const { isInternalUser } = userAuth;
  const [files, setFiles] = useState<PostArtifact[]>([]);

  const [includedDocuments, setIncludedDocuments] = useState<DocumentSearchResponse['items']>([]);

  const [dragging, setDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState<File[]>([]);
  const [isInitialStateLoaded, setIsInitialStateLoaded] = useState(false);

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>, index: number): void => {
    setFiles((prevState: PostArtifact[]) => {
      const newState = prevState.map((el: PostArtifact, i: number) =>
        i === index ? { ...el, description: e.target.value } : el,
      );

      return [...newState];
    });
  };

  const handleRemove = (index: number) => {
    setFiles((prevState: PostArtifact[]) => prevState.filter((el: PostArtifact, i: number) => i !== index));
    setInvalidFiles([]);
  };

  const filesDatagridRows = files.map((el, index) => ({
    ...el,
    index,
    handleDescriptionChange,
    handleRemove,
  }));

  const fileAmountExceeded = useCallback(
    (index: number) => {
      if (files.length + uploadedFiles.length + index < maxFiles) {
        return false;
      }
      return true;
    },
    [files.length, maxFiles, uploadedFiles.length],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const fileList = e.target.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i += 1) {
        const file = fileList[i];
        if (file) {
          if (!fileAmountExceeded(i)) {
            const selectedFiles = getPostArtifact(file);
            setFiles((prevState) => {
              return [...prevState, ...selectedFiles];
            });
          } else {
            setInvalidFiles((prev: File[]) => [...prev, file]);
          }
        }
      }
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const fileList = e.dataTransfer.items;
    for (let i = 0; i < fileList.length; i += 1) {
      if (fileList[i].kind === 'file') {
        const file = fileList[i].getAsFile();
        if (file) {
          if (!fileAmountExceeded(i)) {
            const selectedFiles = getPostArtifact(file);
            setFiles((prevState) => [...prevState, ...selectedFiles]);
          } else {
            setInvalidFiles((prev: File[]) => [...prev, file]);
          }
        }
      }
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDataTransfer(e)) {
      setDragging(true);
    }
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleClose = (e?: SyntheticEvent) => {
    setFiles([]);
    setInvalidFiles([]);
    handleCloseDialog(e);
  };

  const submitFiles = async (e?: SyntheticEvent) => {
    setIsLoading(true);
    try {
      onDone({
        uploadedFiles: files,
        includedDocuments,
      });
    } finally {
      setIsLoading(false);
      handleClose(e);
    }
  };

  useEffect(() => {
    if (!isInitialStateLoaded) {
      setIsInitialStateLoaded(true);
      if (draggedFiles?.length > 0) {
        const selectedFiles: PostArtifact[] = [];
        for (let i = 0; i < draggedFiles.length; i += 1) {
          const file = draggedFiles[i];
          if (!fileAmountExceeded(i)) {
            const [result] = getPostArtifact(file);
            selectedFiles.push(result);
          } else {
            setInvalidFiles((prev: File[]) => [...prev, file]);
          }
        }
        setFiles(selectedFiles);
      }
      resetDraggedFiles?.();
    }
  }, [draggedFiles, fileAmountExceeded, isInitialStateLoaded, resetDraggedFiles]);

  return (
    <Dialog
      open={open}
      size='large'
      title={maxFiles ? 'Attach Files' : 'Attach File'}
      handleCloseDialog={handleClose}
      primaryBtnText='Done'
      isPrimaryBtnLoading={isLoading}
      primaryBtnAction={submitFiles}
      isPrimaryBtnDisabled={files.length === 0 && includedDocuments?.length === 0}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleClose}
      sx={(theme) => ({
        '& .MuiDialogContent-root': {
          backgroundColor:
            theme.palette.theme === 'lio' ? theme.palette.background.main : `${theme.palette.background.main}20`,
        },
      })}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isInternalUser && !hideIncludedDocumentsSection ? (
          <IncludeDocuments quoteId={quoteId} setIncludedDocuments={setIncludedDocuments} />
        ) : null}
        <UploadFilesView
          files={filesDatagridRows}
          invalidFiles={invalidFiles}
          handleChange={handleChange}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          maxFilesAmount={maxFiles}
          dragging={dragging}
        />
        {additionalContent ? (
          <Grid size={{ xs: 12 }} sx={{ mt: 1, px: 3 }}>
            {additionalContent}
          </Grid>
        ) : null}
      </Grid>
    </Dialog>
  );
};
